import { safeSessionStorage, safeStorage } from './safe-storage';

const CHUNK_LOAD_ERROR_RELOAD_AT_KEY = '__chunk_load_error_reload_at__';
const CHUNK_LOAD_ERROR_RELOAD_COOLDOWN = 5 * 60 * 1000;
const CHUNK_LOAD_ERROR_SESSION_RELOAD_KEY = '__chunk_load_error_reloaded_in_session__';

type WindowLike = Pick<Window, 'addEventListener' | 'removeEventListener' | 'location'>;

export const isChunkLoadError = (message?: string | null): boolean => {
  if (!message) {
    return false;
  }

  return [
    /module factory is not available/i,
    /ChunkLoadError/i,
    /Loading chunk [^\s]+ failed/i,
    /Loading CSS chunk [^\s]+ failed/i,
  ].some((pattern) => pattern.test(message));
};

// 老 tab / stale HTML 场景下，浏览器加载到一个已被新 release 移除依赖的旧 chunk。
// chunk 本身 200 加载成功，但顶层 `const { X } = dep.default;` 因为 dep 已从新版本移除、
// interop 后 `dep.default` 为 undefined，在 module evaluation 阶段直接 TypeError。
// 这类错误无法通过 ChunkLoadError 分支识别，堆栈里 in-app 顶帧一定指向 `_next/static/chunks/`。
// 命中就走一次性 reload 兜底，让用户拿到新版本 HTML + 新 chunk。
//
// 判定收敛：仅当堆栈中同一帧既标注 `module evaluation`（或等价的 `(evaluate)` / `(module factory)`）
// 且文件位于 `_next/static/chunks/` 时才命中。业务组件渲染报错虽然文件也会打包进 chunks，
// 但顶帧一般是具体的函数/组件名或 webpack-internal 前缀，不会带 module-evaluation 标记，避免误伤触发整页 reload。
//
// 参考 Sentry vidu-saas #34933/#34859/#34856（`Cannot destructure property 'POSITIONS' of 'd.default'`）
// 根因：refactor(vidu-next): replace xgplayer with native video 之后，旧 tab 上残留的
// `2.18.51-sg/_next/static/chunks/0s~s43560cajs.js` 顶层仍尝试 destructure xgplayer POSITIONS。
const MODULE_EVAL_FRAME_PATTERN = /(?:module evaluation|\(evaluate\)|module factory)[^\n]*_next\/static\/chunks\//i;

export const isStaleChunkModuleEvalError = (message?: string | null, stack?: string | null): boolean => {
  if (!message || !stack) {
    return false;
  }

  const messageMatches = [
    /Cannot destructure property .+ of .+ as it is undefined/i,
    /Cannot read propert(?:y|ies) (?:.+ )?of undefined/i,
    /undefined is not an object \(evaluating/i,
  ].some((pattern) => pattern.test(message));

  if (!messageMatches) {
    return false;
  }

  return stack.split('\n').some((line) => MODULE_EVAL_FRAME_PATTERN.test(line));
};

const getNow = () => Date.now();

const getLastReloadAt = (): number => {
  const value = safeStorage.getItem(CHUNK_LOAD_ERROR_RELOAD_AT_KEY);
  if (!value) {
    return 0;
  }

  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const hasReloadedInCurrentSession = (): boolean =>
  safeSessionStorage.getItem(CHUNK_LOAD_ERROR_SESSION_RELOAD_KEY) === '1';

export const shouldReloadForChunkLoadError = (now = getNow()): boolean =>
  !hasReloadedInCurrentSession() && now - getLastReloadAt() >= CHUNK_LOAD_ERROR_RELOAD_COOLDOWN;

export const markChunkLoadErrorReload = (now = getNow()): boolean => {
  const isReloadAtStored = safeStorage.setItem(CHUNK_LOAD_ERROR_RELOAD_AT_KEY, String(now));
  const isSessionReloadStored = safeSessionStorage.setItem(CHUNK_LOAD_ERROR_SESSION_RELOAD_KEY, '1');

  return isReloadAtStored && isSessionReloadStored;
};

export const tryRecoverChunkLoadError = (windowTarget: WindowLike = window): boolean => {
  if (!shouldReloadForChunkLoadError()) {
    return false;
  }

  if (!markChunkLoadErrorReload()) {
    return false;
  }

  windowTarget.location.reload();
  return true;
};

const getErrorMessage = (error: ErrorEvent): string | undefined => error.error?.message ?? error.message;

const getErrorStack = (event: ErrorEvent): string | undefined => event.error?.stack;

const getRejectionMessage = (event: PromiseRejectionEvent): string | undefined => {
  const { reason } = event;

  if (typeof reason === 'string') {
    return reason;
  }

  if (reason instanceof Error) {
    return reason.message;
  }

  if (typeof reason === 'object' && reason !== null && 'message' in reason && typeof reason.message === 'string') {
    return reason.message;
  }

  return undefined;
};

const getRejectionStack = (event: PromiseRejectionEvent): string | undefined => {
  const { reason } = event;

  if (reason instanceof Error) {
    return reason.stack;
  }

  if (typeof reason === 'object' && reason !== null && 'stack' in reason && typeof reason.stack === 'string') {
    return reason.stack;
  }

  return undefined;
};

const handleChunkLoadError = (windowTarget: WindowLike, message?: string, stack?: string): void => {
  if (isChunkLoadError(message) || isStaleChunkModuleEvalError(message, stack)) {
    tryRecoverChunkLoadError(windowTarget);
  }
};

export const setupChunkLoadErrorRecovery = (windowTarget: WindowLike = window): (() => void) => {
  if (process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'prod') {
    return () => {};
  }

  const onError = (event: ErrorEvent) => {
    handleChunkLoadError(windowTarget, getErrorMessage(event), getErrorStack(event));
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    handleChunkLoadError(windowTarget, getRejectionMessage(event), getRejectionStack(event));
  };

  windowTarget.addEventListener('error', onError);
  windowTarget.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    windowTarget.removeEventListener('error', onError);
    windowTarget.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
};
